import { OnSetup } from "@hooks/useAppSetup/types";
import { revenueCatIsSetup } from "@hooks/useAppSetup/useAppSetupStore";
import { useMe } from "@hooks/trpc/useMe";
import { useAuth } from "@utils/firebaseAuth/auth";
import { useQuery } from "@tanstack/react-query";
import { RouterOutputs } from "@utils/trpc";
import { useCallback, useEffect, useMemo } from "react";
import { Platform } from "react-native";
import Purchases, { LOG_LEVEL, PurchasesPackage } from "react-native-purchases";
import config from "./config";
import {
  reportRevenueCatError,
  isRevenueCatNetworkError,
  type RevenueCatSurface,
  toSubscribeErrorResult,
  type RevenueCatSubscribeResult,
} from "@utils/revenuecatErrors";
import dayjs, { type ManipulateType } from "dayjs";
import { isDebugMode, isDevEnv } from "@utils/envs";
import { useData } from "../runtime/provider";
import {
  isPremiumSchema,
  monthlyOfferingSchema,
  trialInfoSchema,
  type TrialInfo,
} from "@utils/revenuecatSchemas";
import { capabilities } from "../capabilities";

/**
 * Stable react-query keys for the two RevenueCat-backed caches. Bare
 * arrays — these are not tRPC routes, so no `getQueryKey` involved.
 */
const IS_PREMIUM_QUERY_KEY = ["isPremium"] as const;
const MONTHLY_OFFERING_QUERY_KEY = ["monthlyOffering"] as const;
const ANNUAL_OFFERING_QUERY_KEY = ["annualOffering"] as const;
const TRIAL_INFO_QUERY_KEY = ["trialInfo"] as const;

const isRevenueCatAllowed = () => capabilities.allowed("revenuecat");

type User = RouterOutputs["user"]["me"];
type UseRevenueCatOptions = {
  surface?: RevenueCatSurface;
  reportOfferingErrors?: boolean;
};

let configurePromise: Promise<boolean> | null = null;
let loginPromise: Promise<void> | null = null;
let loggedInRevenueCatUserId: string | null = null;

const getRevenueCatApiKey = () => {
  if (Platform.OS === "web") {
    return config.REVENUECAT_API_KEY.web;
  }

  return config.REVENUECAT_API_KEY[Platform.OS === "ios" ? "ios" : "android"];
};

const logRevenueCatMessage = (logLevel: LOG_LEVEL, message: string) => {
  const formattedMessage = `[RevenueCat] ${message}`;
  switch (logLevel) {
    case LOG_LEVEL.ERROR:
      console.error(formattedMessage);
      break;
    case LOG_LEVEL.WARN:
      console.warn(formattedMessage);
      break;
    default:
      console.log(formattedMessage);
  }
};

const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const getProductDetails = (offering?: PurchasesPackage | null) => {
  const product = offering?.product;
  const trial = product?.introPrice;
  const hasFreeTrial = trial?.price === 0;
  const periodNumberOfUnits = trial?.periodNumberOfUnits;
  const periodUnit = trial?.periodUnit?.toLowerCase();

  return {
    introPrice: { periodNumberOfUnits, periodUnit },
    hasFreeTrial,
    price: product?.price,
    priceString: product?.priceString,
    currencyCode: product?.currencyCode,
    chargeDate:
      hasFreeTrial && periodNumberOfUnits && periodUnit
        ? dayjs()
            .add(periodNumberOfUnits, periodUnit as ManipulateType)
            .toISOString()
        : undefined,
  };
};

const waitForRevenueCatConfigured = async () => {
  for (let attempt = 0; attempt < 10; attempt += 1) {
    if (await Purchases.isConfigured()) {
      return true;
    }

    await delay(50);
  }

  return Purchases.isConfigured();
};

const ensureRevenueCatConfigured = async (): Promise<boolean> => {
  if (!isRevenueCatAllowed()) {
    return false;
  }

  if (await Purchases.isConfigured()) {
    return true;
  }

  if (!configurePromise) {
    configurePromise = (async () => {
      Purchases.setLogHandler(logRevenueCatMessage);
      console.log("::useRevenueCat setup:: setting up");
      await Purchases.setLogLevel(LOG_LEVEL.WARN);

      if (await Purchases.isConfigured()) {
        return true;
      }

      console.log("::useRevenueCat setup:: configuring");
      Purchases.configure({
        apiKey: getRevenueCatApiKey(),
      });

      const configured = await waitForRevenueCatConfigured();
      if (!configured) {
        throw new Error("RevenueCat configure did not create a singleton");
      }

      return true;
    })().finally(() => {
      configurePromise = null;
    });
  }

  return configurePromise;
};

const ensureRevenueCatUser = async (user: User | undefined) => {
  if (!user) {
    return false;
  }

  if (!(await ensureRevenueCatConfigured())) {
    return false;
  }

  const userId = user.id;
  if (loggedInRevenueCatUserId === userId) {
    return true;
  }

  if (!loginPromise) {
    loginPromise = (async () => {
      const { created } = await Purchases.logIn(userId);
      loggedInRevenueCatUserId = userId;
      console.log("::useRevenueCat login:: new user?: ", created);
      await Purchases.setEmail(user.email);
    })().finally(() => {
      loginPromise = null;
    });
  }

  await loginPromise;
  return loggedInRevenueCatUserId === userId;
};

const logoutRevenueCatUser = async () => {
  if (!isRevenueCatAllowed()) {
    return;
  }

  if (!(await Purchases.isConfigured())) {
    return;
  }

  const isAnonymous = await Purchases.isAnonymous();
  if (isAnonymous) {
    loggedInRevenueCatUserId = null;
    return;
  }

  console.log("::useRevenueCat logout:: logging out");
  await Purchases.logOut();
  loggedInRevenueCatUserId = null;
};

/**
 * Interact with the RevenueCat API. This is used to manage subscriptions and offers.
 */
const useRevenueCat = (
  onSetupOrOptions?: OnSetup | UseRevenueCatOptions,
  maybeOptions?: UseRevenueCatOptions,
) => {
  const onSetup =
    typeof onSetupOrOptions === "function" ? onSetupOrOptions : undefined;
  const options =
    typeof onSetupOrOptions === "function" ? maybeOptions : onSetupOrOptions;
  const surface = options?.surface ?? "unknown";
  const reportOfferingErrors = options?.reportOfferingErrors ?? true;

  // Answers that settle after the session ends write only to that session.
  const data = useData();
  const {
    query: { data: user },
  } = useMe();
  const { status: authStatus } = useAuth();

  const reportError = useCallback(
    (
      error: unknown,
      reportOptions: Parameters<typeof reportRevenueCatError>[1],
    ) => {
      reportRevenueCatError(error, {
        ...reportOptions,
        surface: reportOptions.surface ?? surface,
      });
    },
    [surface],
  );

  const logout = useCallback(async () => {
    try {
      await logoutRevenueCatUser();
    } catch (e) {
      console.error("::useRevenueCat logout:: error logging out: ", e);
      reportError(e, { operation: "revenuecat-logout" });
    }
  }, [reportError]);

  useEffect(() => {
    if (user) {
      ensureRevenueCatUser(user)
        .then((isRevenueCatUserReady) => {
          if (isRevenueCatUserReady) {
            data.queryClient
              .invalidateQueries({
                queryKey: IS_PREMIUM_QUERY_KEY,
              })
              .catch((error) => {
                reportError(error, {
                  operation: "revenuecat-refresh-premium-after-login",
                });
              });
          }
        })
        .catch((error) => {
          reportError(error, { operation: "revenuecat-login" });
        });
      return;
    }

    if (authStatus !== "no-user") {
      return;
    }

    logout().catch((error) => {
      reportError(error, { operation: "revenuecat-logout" });
    });
  }, [user, authStatus, logout, reportError, data]);

  const getMonthlyOffering =
    useCallback(async (): Promise<PurchasesPackage | null> => {
      if (!(await ensureRevenueCatConfigured())) {
        return null;
      }

      try {
        console.log("::useRevenueCat getMonthlyOffering:: getting offerings");
        const offerings = await Purchases.getOfferings();
        console.log("::useRevenueCat getMonthlyOffering:: got offerings ");
        if (offerings.current?.monthly) {
          return offerings.current.monthly;
        }
      } catch (e) {
        const isStartupOfferingFetch = surface === "startup";

        // Offline is expected: the saved offering is shown and the outage
        // is a breadcrumb, not an error.
        if (reportOfferingErrors || isStartupOfferingFetch) {
          reportError(e, {
            channel: isRevenueCatNetworkError(e)
              ? "breadcrumb"
              : isStartupOfferingFetch
                ? "warning"
                : "error",
            operation: "revenuecat-get-offerings",
          });
        }

        if (!isDebugMode() && !isDevEnv()) {
          console.error(
            "::useRevenueCat getMonthlyOffering:: error getting offerings: ",
            e,
          );
        }
        throw e;
      }

      return null;
    }, [reportError, reportOfferingErrors, surface]);

  const monthlyOffering = useQuery({
    ...data.cache.durableQueryOptions({
      queryKey: MONTHLY_OFFERING_QUERY_KEY,
      queryFn: getMonthlyOffering,
      // The schema is intentionally permissive (passthrough) — third-party
      // SDK shape, we only care that *something* was cached.
      // Cast is safe: passthrough schema accepts any object; the real SDK
      // type is enforced by the `as ReturnType<…>` on the useQuery call.
      schema:
        monthlyOfferingSchema as unknown as import("zod").ZodType<PurchasesPackage | null>,
    }),
  }) as ReturnType<typeof useQuery<PurchasesPackage | null>>;

  const getAnnualOffering =
    useCallback(async (): Promise<PurchasesPackage | null> => {
      if (!(await ensureRevenueCatConfigured())) {
        return null;
      }

      try {
        const offerings = await Purchases.getOfferings();
        return offerings.current?.annual ?? null;
      } catch (e) {
        if (reportOfferingErrors) {
          reportError(e, {
            ...(isRevenueCatNetworkError(e) ? { channel: "breadcrumb" } : {}),
            operation: "revenuecat-get-annual-offering",
          });
        }
        throw e;
      }
    }, [reportError, reportOfferingErrors]);

  const annualOffering = useQuery({
    ...data.cache.durableQueryOptions({
      queryKey: ANNUAL_OFFERING_QUERY_KEY,
      queryFn: getAnnualOffering,
      schema:
        monthlyOfferingSchema as unknown as import("zod").ZodType<PurchasesPackage | null>,
    }),
    enabled: surface === "paywall",
  }) as ReturnType<typeof useQuery<PurchasesPackage | null>>;

  const userHasActiveSubscription = useCallback(async (): Promise<boolean> => {
    if (!user) {
      data.cache.setQueryDataDurable<boolean>(
        data.queryClient,
        IS_PREMIUM_QUERY_KEY,
        () => false,
      );
      return false;
    }

    if (!(await ensureRevenueCatUser(user))) {
      data.cache.setQueryDataDurable<boolean>(
        data.queryClient,
        IS_PREMIUM_QUERY_KEY,
        () => false,
      );
      return false;
    }

    try {
      const { entitlements } = await Purchases.getCustomerInfo();
      const isPremium = typeof entitlements.active.Premium !== "undefined";
      data.cache.setQueryDataDurable<boolean>(
        data.queryClient,
        IS_PREMIUM_QUERY_KEY,
        () => isPremium,
      );
      return isPremium;
    } catch (e) {
      reportError(e, {
        operation: "revenuecat-get-customer-info",
      });
      data.cache.setQueryDataDurable<boolean>(
        data.queryClient,
        IS_PREMIUM_QUERY_KEY,
        () => false,
      );
      return false;
    }
  }, [data, reportError, user]);

  const getUserTrialInfo = useCallback(async (): Promise<TrialInfo> => {
    if (!user) {
      data.cache.setQueryDataDurable<TrialInfo>(
        data.queryClient,
        TRIAL_INFO_QUERY_KEY,
        () => null,
      );
      return null;
    }

    if (!(await ensureRevenueCatUser(user))) {
      data.cache.setQueryDataDurable<TrialInfo>(
        data.queryClient,
        TRIAL_INFO_QUERY_KEY,
        () => null,
      );
      return null;
    }

    try {
      const { entitlements } = await Purchases.getCustomerInfo();
      const premium = entitlements.active.Premium;
      if (!premium) {
        data.cache.setQueryDataDurable<TrialInfo>(
          data.queryClient,
          TRIAL_INFO_QUERY_KEY,
          () => null,
        );
        return null;
      }

      const info: TrialInfo = {
        periodType: premium.periodType,
        expirationDate: premium.expirationDate,
      };
      data.cache.setQueryDataDurable<TrialInfo>(
        data.queryClient,
        TRIAL_INFO_QUERY_KEY,
        () => info,
      );
      return info;
    } catch (e) {
      reportError(e, {
        operation: "revenuecat-get-trial-info",
      });
      data.cache.setQueryDataDurable<TrialInfo>(
        data.queryClient,
        TRIAL_INFO_QUERY_KEY,
        () => null,
      );
      return null;
    }
  }, [data, reportError, user]);

  const subscribe = async (
    packageToPurchase: PurchasesPackage,
  ): Promise<RevenueCatSubscribeResult> => {
    if (!user) {
      return {
        success: false,
        error: "You need to sign in before subscribing",
      };
    }

    if (!(await ensureRevenueCatUser(user))) {
      return {
        success: false,
        error: "RevenueCat is unavailable in preview environments",
      };
    }

    try {
      await Purchases.purchasePackage(packageToPurchase);

      const userHasSubscription = await userHasActiveSubscription();

      if (userHasSubscription) {
        return { success: true };
      }

      const missingEntitlementError = new Error(
        "Purchase completed without active Premium entitlement",
      );
      reportError(missingEntitlementError, {
        operation: "revenuecat-subscribe-missing-entitlement",
        ...(surface === "paywall"
          ? {
              area: "paywall",
              fingerprint: ["paywall", "missing-entitlement"],
              operation: "paywall-missing-entitlement",
              tags: {
                paywall_phase: "entitlement",
              },
            }
          : {}),
      });
      return {
        success: false,
        error: missingEntitlementError.message,
      };
    } catch (e) {
      return toSubscribeErrorResult(e, {
        operation:
          surface === "paywall" ? "paywall-purchase" : "revenuecat-subscribe",
        phase: "purchase",
        surface,
      });
    }
  };

  const isPremium = useQuery(
    data.cache.durableQueryOptions({
      queryKey: IS_PREMIUM_QUERY_KEY,
      queryFn: userHasActiveSubscription,
      schema: isPremiumSchema,
    }),
  );

  const trialInfo = useQuery(
    data.cache.durableQueryOptions({
      queryKey: TRIAL_INFO_QUERY_KEY,
      queryFn: getUserTrialInfo,
      schema: trialInfoSchema,
    }),
  ) as ReturnType<typeof useQuery<TrialInfo>>;

  const getSubscriptionManagementUrl = async (): Promise<string | null> => {
    if (!user || !(await ensureRevenueCatUser(user))) {
      return null;
    }

    try {
      const customerInfo = await Purchases.getCustomerInfo();
      return customerInfo.managementURL;
    } catch (e) {
      reportError(e, {
        operation: "revenuecat-get-management-url",
      });
      return null;
    }
  };

  const setup = useCallback(
    async (onSetup?: OnSetup) => {
      if (!isRevenueCatAllowed()) {
        data.cache.setQueryDataDurable<PurchasesPackage | null>(
          data.queryClient,
          MONTHLY_OFFERING_QUERY_KEY,
          () => null,
        );
        data.cache.setQueryDataDurable<boolean>(
          data.queryClient,
          IS_PREMIUM_QUERY_KEY,
          () => false,
        );
        data.cache.setQueryDataDurable<TrialInfo>(
          data.queryClient,
          TRIAL_INFO_QUERY_KEY,
          () => null,
        );
        onSetup?.(true);
        return;
      }

      try {
        try {
          await ensureRevenueCatConfigured();
        } catch (e) {
          console.warn("::useRevenueCat setup:: error configuring: ", e);
          reportError(e, { operation: "revenuecat-configure" });
        }

        // At startup, or offline, keep the saved offering and finish setup.
        const offerings = await getMonthlyOffering().catch((e) => {
          if (surface === "startup" || isRevenueCatNetworkError(e)) {
            return undefined;
          }

          throw e;
        });

        if (offerings !== undefined) {
          data.cache.setQueryDataDurable<PurchasesPackage | null>(
            data.queryClient,
            MONTHLY_OFFERING_QUERY_KEY,
            () => offerings,
          );
        }

        if (user) {
          await userHasActiveSubscription().then((hasSubscription) => {
            data.cache.setQueryDataDurable<boolean>(
              data.queryClient,
              IS_PREMIUM_QUERY_KEY,
              () => hasSubscription,
            );
          });
          await getUserTrialInfo();
        } else {
          data.cache.setQueryDataDurable<boolean>(
            data.queryClient,
            IS_PREMIUM_QUERY_KEY,
            () => false,
          );
          data.cache.setQueryDataDurable<TrialInfo>(
            data.queryClient,
            TRIAL_INFO_QUERY_KEY,
            () => null,
          );
        }
      } catch (e) {
        console.error("::useRevenueCat setup:: error: ", e);
        reportError(e, { operation: "revenuecat-setup" });
      }

      onSetup?.(true);
    },
    [
      data,
      getMonthlyOffering,
      getUserTrialInfo,
      reportError,
      surface,
      user,
      userHasActiveSubscription,
    ],
  );

  useEffect(() => {
    if (!revenueCatIsSetup()) {
      setup(onSetup).catch((error) => {
        reportError(error, { operation: "revenuecat-setup" });
      });
    }
  }, [onSetup, reportError, setup]);

  const product = useMemo(
    () => getProductDetails(monthlyOffering.data),
    [monthlyOffering.data],
  );

  const annualProduct = useMemo(
    () => getProductDetails(annualOffering.data),
    [annualOffering.data],
  );

  return {
    getSubscriptionManagementUrl,
    subscribe,
    monthlyOffering,
    annualOffering,
    isPremium,
    trialInfo,
    product,
    annualProduct,
    // Point-in-time entitlement check (fresh getCustomerInfo, not the cached
    // isPremium query) — for decisions that must not race a stale cache,
    // e.g. skipping the onboarding paywall for already-entitled users.
    userHasActiveSubscription,
  };
};

export default useRevenueCat;
